import React from "react";
import {
  BillingStartFromInterval,
  CheckOutDisplayTypes,
  ItemBillingStartAfterInterval,
  ItemBillingType,
  PricingModel,
  RecurringBillingInterval,
} from "../enums";
import {
  CustomPricingModel,
  PackagePricingModel,
  PriceTermSettings,
  RecurrencePriceSettings,
  StandardPricingModel,
  TierPricingModel,
} from "./Price.model";
import { PricingPageInputModel } from "./PriceInputs.model";
import { PurchaseItemsPriceModel } from "./PurchaseItemsPriceModel";
import { InventorySettings, ProductAttributeInputModel } from "./ProductModel";
import {
  DonationIncludedSectionModel,
  RelatedItemsCategoriesModel,
  RelatedItemsPriceCategoriesModel,
  RelatedSectionModel,
} from "./DonationAndItemModel";
import { BeneficiaryModel } from "./BeneficiaryModel";
import { MediaModel } from "./MediaModel";
import { EditorState } from "draft-js";
import { autorun } from "mobx";
import {
  IAPICustomerFacingCheckOutItemModel,
  IAPIGetEditDonationModel,
} from "../interfaces";
import { CheckOutItemsModel } from "./CheckOutSellPageModel";
import { ItemDetailInputModel } from "./ItemDetailInputModel";
import {
  CoreInputModelUtil,
  CustomLabel,
  getNewErrors,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { CustomOptionModel } from "./CustomOptionModel";
import { AccountSettingsStore } from "../store";
import { AmountType } from "../enums/Promotion.enum";
import { PromotionModel } from "./PromotionModels";

export class PriceDescriptions {
  priceId: string;
  originalPrice: string;
  price: string;
  renewDescription: React.ReactNode;
  priceInNumber: number;
  priceWithDiscountInNumber?: number;
  priceWithDiscount?: string;
  priceDescription?: string | React.ReactNode;
  originalPriceInNumber?: number;
  setupFee: number;
  quantity: number;
  termPlusRecurringRenewalPrice: number;
  //This will hold immediate price from recurrence and applies to TERMS + RECURRING
  totalImmediatePriceForTPR: number;
  isStartImmediately: boolean;
  discount: number;
  surchargePercentage?: number;
  constructor(data?: Partial<PriceDescriptions>) {
    this.priceId = data?.priceId || "";
    this.originalPrice = data?.originalPrice || "";
    this.price = data?.price || "";
    this.renewDescription = data?.renewDescription || <></>;
    this.priceInNumber = data?.priceInNumber || 0;
    this.priceWithDiscountInNumber = data?.priceWithDiscountInNumber;
    this.priceWithDiscount = data?.priceWithDiscount;
    this.priceDescription = data?.priceDescription;
    this.originalPriceInNumber = data?.originalPriceInNumber;
    this.setupFee = data?.setupFee || 0;
    this.quantity = data?.quantity || 1;
    this.totalImmediatePriceForTPR = 0;
    this.termPlusRecurringRenewalPrice =
      data?.termPlusRecurringRenewalPrice || 0;
    this.isStartImmediately = data?.isStartImmediately || false;
    this.surchargePercentage = data?.surchargePercentage
      ? data.surchargePercentage
      : 0;
    this.discount = data?.discount || 0;
  }

  get subTotal() {
    const setupCharges = this.isStartImmediately ? this.setupFee : 0;
    return (
      this.totalImmediatePriceForTPR +
      (this.priceInNumber + setupCharges) * this.quantity
    );
  }

  get totalPriceWithDiscountSurcharge() {
    return (
      this.subTotal -
      this.discount * this.quantity +
      (((this.surchargePercentage || 0) * this.priceInNumber) / 100) *
        this.quantity
    );
  }

  get subTotalWithoutSetupFee() {
    return this.totalImmediatePriceForTPR + this.priceInNumber * this.quantity;
  }

  get subTotalForIncludedItems() {
    const setupCharges = this.isStartImmediately ? this.setupFee : 0;
    return (
      this.totalImmediatePriceForTPR +
      (this.priceInNumber + setupCharges) * (this.quantity - 1)
    );
  }
}

export function getRecurrenceRenewDescription(
  recurrence: RecurrencePriceSettings,
  price: string,
) {
  const renewDescription = `Renews after ${recurrence.billingIntervalCount} ${recurrence.billingInterval} at`;
  const priceDescription = (
    <>
      <b>{price}</b>{" "}
      {/* {recurrence.billingIntervalCount > 1
              ? `every ${recurrence.billingIntervalCount} ${recurrence.billingInterval}`
              : `every ${recurrence.billingInterval}`} */}
    </>
  );
  switch (recurrence.billingInterval) {
    case "Day":
      return (
        <div>
          {renewDescription} <br />
          {priceDescription}
        </div>
      );
    case "Week":
      return (
        <div>
          {renewDescription}
          <br />
          {priceDescription}
        </div>
      );
    case "Month":
      return (
        <div>
          {renewDescription}
          <br />
          {priceDescription}
        </div>
      );
    case "Year":
      return (
        <div>
          {renewDescription}
          <br />
          {priceDescription}
        </div>
      );
    default:
      return <></>;
  }
}
export function getPriceDescription(
  recurrence: RecurrencePriceSettings,
  price: string,
) {
  const priceDescription = (
    <>
      <b>{price}</b>{" "}
      {recurrence.billingIntervalCount > 1
        ? `every ${recurrence.billingIntervalCount} ${recurrence.billingInterval}`
        : `every ${recurrence.billingInterval}`}
    </>
  );
  return priceDescription;
}

//if recurrecne price starts immediately

export function getPricingDetails(
  isStartImmediately: boolean,
  quantity: number,
  pricingModel: PricingModel,
  standardPricing?: StandardPricingModel | null,
  packagePricing?: PackagePricingModel | null,
  customPricing?: CustomPricingModel | null,
  tierPricing?: TierPricingModel | null,
  volumePricing?: TierPricingModel | null,
  recurrence?: RecurrencePriceSettings,
  isRecurrence = false,
  customAmount?: number,
  surchargePercentage?: number,
  promotionRelatedData?: PromotionModel,
  isDiscountInPercentageWithMax?: boolean,
): PriceDescriptions | null {
  let price = "";
  let priceInNumber = 0;
  let setupFee = 0;
  let originalPriceInNumber = 0;
  let discount = 0;
  let priceWithSetupFee = 0;
  switch (pricingModel) {
    case "Standard":
      priceInNumber = standardPricing?.price || 0;
      setupFee = standardPricing?.setUpFee || 0;
      priceWithSetupFee = priceInNumber + setupFee;
      if (promotionRelatedData) {
        if (promotionRelatedData.amountType === AmountType.Amount) {
          discount = promotionRelatedData.amount;
          if (discount > priceWithSetupFee * quantity) {
            discount = priceWithSetupFee * quantity;
          }
        } else {
          discount =
            ((priceWithSetupFee * promotionRelatedData.amount) / 100) *
            quantity;
          if (
            discount > promotionRelatedData.maximumAmount &&
            isDiscountInPercentageWithMax
          ) {
            discount = promotionRelatedData.maximumAmount;
          }
        }
      }
      if (promotionRelatedData) {
        originalPriceInNumber = standardPricing?.price
          ? (standardPricing.price || 0) * quantity
          : 0;
      } else {
        originalPriceInNumber = standardPricing?.originalPrice
          ? (priceInNumber || 0) * quantity
          : 0;
      }
      price = standardPricing?.price
        ? "$" + AccountSettingsStore.formatAmount(priceInNumber, true)
        : "";
      return new PriceDescriptions({
        isStartImmediately: isStartImmediately,
        quantity: quantity,
        originalPrice:
          "$" + AccountSettingsStore.formatAmount(originalPriceInNumber, true),
        price: price,
        setupFee: setupFee,
        discount: discount,
        //totalPriceInNumber: priceInNumber,
        priceInNumber: priceInNumber,
        originalPriceInNumber: originalPriceInNumber,
        renewDescription:
          isRecurrence && recurrence ? (
            getRecurrenceRenewDescription(recurrence, price)
          ) : (
            <></>
          ),
        priceDescription:
          isRecurrence && recurrence
            ? getPriceDescription(recurrence, price)
            : price,
        surchargePercentage: surchargePercentage,
        priceWithDiscountInNumber: promotionRelatedData
          ? priceInNumber * quantity - discount
          : 0,
        priceWithDiscount: promotionRelatedData
          ? "$" +
            AccountSettingsStore.formatAmount(
              priceInNumber * quantity - discount,
              true,
            )
          : "",
      });
    case "Package":
      let packageIndex = packagePricing?.options.findIndex(
        (x) => x.units >= quantity,
      );
      if (packageIndex === -1) {
        packageIndex = !packagePricing?.options ? -1 : 0;
      }

      packageIndex = packageIndex || 0;

      price =
        packagePricing?.options && packagePricing?.options?.length > 0
          ? "$" +
            AccountSettingsStore.formatAmount(
              packagePricing?.options[packageIndex].price,
              true,
            )
          : "";

      priceInNumber = packagePricing?.options[packageIndex]?.price || 0;

      if (promotionRelatedData) {
        if (promotionRelatedData.amountType === AmountType.Amount) {
          discount = promotionRelatedData.amount;
          if (discount > priceInNumber * quantity) {
            discount = priceInNumber * quantity;
          }
        } else {
          discount =
            ((priceInNumber * promotionRelatedData.amount) / 100) * quantity;
          if (
            discount > promotionRelatedData.maximumAmount &&
            isDiscountInPercentageWithMax
          ) {
            discount = promotionRelatedData.maximumAmount;
          }
        }
      }
      if (promotionRelatedData) {
        originalPriceInNumber = packagePricing?.options[packageIndex]?.price
          ? packagePricing?.options[packageIndex]?.price * quantity
          : 0;
      } else {
        originalPriceInNumber = packagePricing?.options[packageIndex]
          ?.originalPrice
          ? packagePricing?.options[packageIndex]?.originalPrice * quantity
          : 0;
      }

      return new PriceDescriptions({
        isStartImmediately: isStartImmediately,
        quantity: 1,
        originalPrice: originalPriceInNumber
          ? "$" + AccountSettingsStore.formatAmount(originalPriceInNumber, true)
          : "",
        originalPriceInNumber: originalPriceInNumber || 0,
        price: price,
        setupFee: setupFee,
        priceInNumber: priceInNumber,
        discount: discount,
        //totalPriceInNumber: priceInNumber,
        renewDescription:
          isRecurrence && recurrence ? (
            getRecurrenceRenewDescription(recurrence, price)
          ) : (
            <></>
          ),
        priceDescription:
          isRecurrence && recurrence
            ? getPriceDescription(recurrence, price)
            : price,
        surchargePercentage: surchargePercentage,
        priceWithDiscountInNumber: priceInNumber * quantity - discount,
        priceWithDiscount: promotionRelatedData
          ? "$" +
            AccountSettingsStore.formatAmount(
              priceInNumber * quantity - discount,
              true,
            )
          : "",
      });
    case "Volume":
      price = volumePricing?.price
        ? "$" + AccountSettingsStore.formatAmount(volumePricing?.price, true)
        : "";
      let volumeIndex = volumePricing?.tiers.findIndex(
        (x) =>
          x.startValue <= quantity &&
          x.endValue >= quantity &&
          x.endValue !== -1,
      );
      if (volumeIndex === -1) {
        volumeIndex = volumePricing ? volumePricing?.tiers.length - 1 : 0;
      }

      volumeIndex = volumeIndex || 0;
      const discountPercentage =
        volumePricing?.tiers[volumeIndex]?.discountPercentage || 0;
      const volumeDicountedPrice = discountPercentage
        ? (volumePricing?.price || 0) -
          UtilityMethods.calculatePercentageValue(
            volumePricing?.price || 0,
            discountPercentage,
          )
        : volumePricing?.price || 0;
      priceInNumber = volumeDicountedPrice || 0;
      setupFee = volumePricing?.setUpFee || 0;

      if (promotionRelatedData) {
        if (promotionRelatedData.amountType === AmountType.Amount) {
          discount = promotionRelatedData.amount;
          if (discount > priceInNumber * quantity) {
            discount = priceInNumber * quantity;
          }
        } else {
          discount =
            ((priceInNumber * promotionRelatedData.amount) / 100) * quantity;
          if (
            discount > promotionRelatedData.maximumAmount &&
            isDiscountInPercentageWithMax
          ) {
            discount = promotionRelatedData.maximumAmount;
          }
        }
      }
      if (promotionRelatedData) {
        originalPriceInNumber = volumePricing?.price
          ? volumePricing?.price * quantity
          : 0;
      } else {
        originalPriceInNumber = volumePricing?.originalPrice
          ? volumePricing?.originalPrice * quantity
          : 0;
      }
      return new PriceDescriptions({
        isStartImmediately: isStartImmediately,
        quantity: quantity,
        originalPrice: originalPriceInNumber
          ? "$" + AccountSettingsStore.formatAmount(originalPriceInNumber, true)
          : "",
        originalPriceInNumber: originalPriceInNumber || 0,
        price: price,
        priceInNumber: priceInNumber,
        setupFee: setupFee,
        discount: discount,
        //totalPriceInNumber: priceInNumber,
        //calculate discount based on volume price and volume discount
        priceWithDiscountInNumber: priceInNumber * quantity - discount,
        priceWithDiscount: volumeDicountedPrice
          ? "$" +
            AccountSettingsStore.formatAmount(
              priceInNumber * quantity - discount,
              true,
            )
          : "",
        renewDescription:
          isRecurrence && recurrence ? (
            getRecurrenceRenewDescription(recurrence, price)
          ) : (
            <></>
          ),
        priceDescription:
          isRecurrence && recurrence
            ? getPriceDescription(recurrence, price)
            : price,
        surchargePercentage: surchargePercentage,
      });
    case "Tiered":
      if (!tierPricing || !tierPricing.tiers.length) {
        return null;
      }
      let totalPrice = 0;
      let remainingQuantity = quantity;
      for (let i = 0; i < tierPricing.tiers.length; i++) {
        const tier = tierPricing.tiers[i];
        let unitsInTier = 0;

        if (tier.endValue === -1 || tier.endValue === Infinity) {
          unitsInTier = remainingQuantity;
        } else {
          unitsInTier = Math.min(
            remainingQuantity,
            tier.endValue - tier.startValue + 1,
          );
        }

        if (unitsInTier > 0) {
          const tierPrice =
            tierPricing.price -
            UtilityMethods.calculatePercentageValue(
              tierPricing.price,
              tier.discountPercentage,
            );

          totalPrice += unitsInTier * tierPrice;
          remainingQuantity -= unitsInTier;
        }

        if (remainingQuantity <= 0) break;
      }

      price = "$" + AccountSettingsStore.formatAmount(totalPrice, true);
      priceInNumber = totalPrice;
      setupFee = tierPricing?.setUpFee || 0;

      if (promotionRelatedData) {
        if (promotionRelatedData.amountType === AmountType.Amount) {
          discount = promotionRelatedData.amount;
          if (discount > priceInNumber * quantity) {
            discount = priceInNumber * quantity;
          }
        } else {
          discount =
            ((priceInNumber * promotionRelatedData.amount) / 100) * quantity;
          if (
            discount > promotionRelatedData.maximumAmount &&
            isDiscountInPercentageWithMax
          ) {
            discount = promotionRelatedData.maximumAmount;
          }
        }
      }
      if (promotionRelatedData) {
        originalPriceInNumber = totalPrice ? totalPrice : 0;
      } else {
        originalPriceInNumber = tierPricing.originalPrice
          ? tierPricing.originalPrice * quantity
          : 0;
      }
      return new PriceDescriptions({
        isStartImmediately: isStartImmediately,
        quantity: 1,
        originalPrice: originalPriceInNumber
          ? "$" + AccountSettingsStore.formatAmount(originalPriceInNumber, true)
          : "",
        originalPriceInNumber: originalPriceInNumber || 0,
        price: price,
        discount: discount,
        priceInNumber: priceInNumber,
        //totalPriceInNumber: priceInNumber,
        priceWithDiscountInNumber: priceInNumber * quantity - discount,
        priceWithDiscount:
          "$" +
          AccountSettingsStore.formatAmount(
            priceInNumber * quantity - discount,
            true,
          ),
        setupFee: setupFee,
        renewDescription:
          isRecurrence && recurrence ? (
            getRecurrenceRenewDescription(recurrence, price)
          ) : (
            <></>
          ),
        priceDescription:
          isRecurrence && recurrence
            ? getPriceDescription(recurrence, price)
            : price,
        surchargePercentage: surchargePercentage,
      });
    case "Custom":
      price = customPricing
        ? "$" +
          AccountSettingsStore.formatAmount(
            customAmount || customPricing?.minimum,
            true,
          )
        : "";

      priceInNumber = customAmount || customPricing?.minimum || 0;
      if (promotionRelatedData) {
        if (promotionRelatedData.amountType === AmountType.Amount) {
          discount = promotionRelatedData.amount;
          if (discount > priceInNumber) {
            discount = priceInNumber;
          }
        } else {
          discount =
            ((priceInNumber * promotionRelatedData.amount) / 100) * quantity;
          if (
            discount > promotionRelatedData.maximumAmount &&
            isDiscountInPercentageWithMax
          ) {
            discount = promotionRelatedData.maximumAmount;
          }
        }
      }
      return new PriceDescriptions({
        isStartImmediately: isStartImmediately,
        quantity: quantity,
        originalPrice: "",
        price: price,
        setupFee: setupFee,
        priceInNumber: priceInNumber,
        discount: discount,
        // totalPriceInNumber: customPricing?.minimum || 0,
        renewDescription:
          isRecurrence && recurrence ? (
            getRecurrenceRenewDescription(recurrence, price)
          ) : (
            <></>
          ),
        priceDescription:
          isRecurrence && recurrence
            ? getPriceDescription(recurrence, price)
            : price,
        surchargePercentage: surchargePercentage,
        priceWithDiscountInNumber: priceInNumber * quantity - discount,
        priceWithDiscount: promotionRelatedData
          ? "$" +
            AccountSettingsStore.formatAmount(
              priceInNumber * quantity - discount,
              true,
            )
          : "",
      });
    default:
      return null;
  }
}

export function getPriceByBillingType(
  itemType: string,
  hasVariants: boolean,
  variants: ProductAttributeInputModel,
  priceId: string,
  billingType: keyof typeof ItemBillingType,
  terms: PriceTermSettings,
  recurrence: RecurrencePriceSettings,
  quantity: number,
  variantIndex?: number,
  customAmount?: number,
  surchargePercentage?: number,
  promotionRelatedData?: PromotionModel,
  isDiscountInPercentageWithMax?: boolean,
) {
  switch (itemType) {
    case "Product":
      if (hasVariants && variants) {
        const index = variantIndex || 0;
        let originalPrice = "";
        let priceInNumber = variants.priceByVariant
          ? parseFloat(variants.options[index]?.price)
          : terms.standardPricing?.price || 0;
        const setUpFee = terms.standardPricing?.setUpFee || 0;
        let discount = 0;
        if (promotionRelatedData) {
          if (promotionRelatedData.amountType === AmountType.Amount) {
            discount = promotionRelatedData.amount;
            if (discount > priceInNumber * quantity) {
              discount = priceInNumber * quantity;
            }
          } else {
            discount =
              ((priceInNumber * promotionRelatedData.amount) / 100) * quantity;
            if (
              discount > promotionRelatedData.maximumAmount &&
              isDiscountInPercentageWithMax
            ) {
              discount = promotionRelatedData.maximumAmount;
            }
          }
        }
        if (promotionRelatedData) {
          originalPrice = terms.standardPricing?.originalPrice
            ? "$" +
              AccountSettingsStore.formatAmount(
                (priceInNumber || 0) * quantity,
                true,
              )
            : "";
        } else {
          originalPrice = terms.standardPricing?.originalPrice
            ? "$" +
              AccountSettingsStore.formatAmount(
                (terms.standardPricing.originalPrice || 0) * quantity,
                true,
              )
            : "";
        }
        switch (terms.pricingModel) {
          case "Standard":
            return new PriceDescriptions({
              priceId: "",
              originalPrice: variants.originalPriceByVariant
                ? "$" +
                  AccountSettingsStore.formatAmount(
                    parseFloat(variants.options[index].originalPrice) *
                      quantity,
                    true,
                  )
                : originalPrice,
              originalPriceInNumber: variants.originalPriceByVariant
                ? parseFloat(variants.options[index].originalPrice) * quantity
                : (terms.standardPricing?.originalPrice || 0) * quantity,
              price:
                "$" + AccountSettingsStore.formatAmount(priceInNumber, true),
              priceInNumber: priceInNumber,
              renewDescription: <></>,
              quantity: quantity,
              setupFee: setUpFee,
              surchargePercentage: surchargePercentage,
              priceWithDiscountInNumber: promotionRelatedData
                ? priceInNumber * quantity - discount
                : 0,
              priceWithDiscount: promotionRelatedData
                ? "$" +
                  AccountSettingsStore.formatAmount(
                    priceInNumber * quantity - discount,
                    true,
                  )
                : "",
              discount: discount,
            });
        }
      }
      break;
  }

  switch (billingType) {
    case ItemBillingType.Term:
      return getPricingDetails(
        true,
        quantity,
        terms.pricingModel,
        terms?.standardPricing,
        terms?.packagePricing,
        terms?.customPricing,
        terms?.tierPricing,
        terms?.volumePricing,
        undefined,
        false,
        customAmount,
        surchargePercentage,
        promotionRelatedData,
        isDiscountInPercentageWithMax,
      );
    case ItemBillingType.Recurring:
      return getPricingDetails(
        recurrence.billingStartInterval ===
          BillingStartFromInterval.Immediately,
        quantity,
        recurrence.pricingModel,
        recurrence?.standardPricing,
        recurrence?.packagePricing,
        recurrence?.customPricing,
        recurrence?.tierPricing,
        recurrence?.volumePricing,
        recurrence,
        true,
        customAmount,
        surchargePercentage,
        promotionRelatedData,
        isDiscountInPercentageWithMax,
      );
    case ItemBillingType.TermPlusRecurring:
      const termsPricing = getPricingDetails(
        true,
        quantity,
        terms.pricingModel,
        terms?.standardPricing,
        terms?.packagePricing,
        terms?.customPricing,
        terms?.tierPricing,
        terms?.volumePricing,
        recurrence,
        true,
        customAmount,
        surchargePercentage,
        promotionRelatedData,
        isDiscountInPercentageWithMax,
      );

      const recurencePricing = getPricingDetails(
        recurrence?.billingStartInterval ===
          BillingStartFromInterval.Immediately,
        quantity,
        recurrence.pricingModel,
        recurrence?.standardPricing,
        recurrence?.packagePricing,
        recurrence?.customPricing,
        recurrence?.tierPricing,
        recurrence?.volumePricing,
        recurrence,
        true,
        customAmount,
        surchargePercentage,
        promotionRelatedData,
        isDiscountInPercentageWithMax,
      );

      if (recurencePricing && termsPricing) {
        termsPricing.termPlusRecurringRenewalPrice =
          recurencePricing?.priceInNumber * quantity;
        termsPricing.discount =
          termsPricing.discount + recurencePricing.discount;
      }

      const immediatleyPayRecurrencePrice =
        recurrence?.billingStartInterval ===
        BillingStartFromInterval.Immediately;

      if (immediatleyPayRecurrencePrice) {
        if (termsPricing && recurencePricing) {
          termsPricing.totalImmediatePriceForTPR = recurencePricing.subTotal;
        }
      }

      return termsPricing;
    default:
      return null;
  }
}

export const getShowOriginalPrice = (
  pricingInput: PricingPageInputModel | null,
): boolean => {
  if (!pricingInput) return false;
  const { billingType, termSettings, recurringSettings } = pricingInput;

  const extractShowOriginalPrice = (
    pricingModel: PricingModel | null,
    settings: any,
  ) => {
    if (!pricingModel) return false;
    switch (pricingModel) {
      case PricingModel.Standard:
        return settings?.standardPricing?.showOriginalPrice ?? false;
      case PricingModel.Volume:
        return settings?.volumePricing?.showOriginalPrice ?? false;
      case PricingModel.Package:
        return settings?.packagePricing?.showOriginalPrice ?? false;
      case PricingModel.Tiered:
        return settings?.tieredPricing?.showOriginalPrice ?? false;
      case PricingModel.Custom:
        return settings?.customPricing?.showOriginalPrice ?? false;
      default:
        return false;
    }
  };

  if (billingType === ItemBillingType.Term) {
    return extractShowOriginalPrice(
      termSettings?.pricingModel || null,
      termSettings,
    );
  } else if (billingType === ItemBillingType.Recurring) {
    return extractShowOriginalPrice(
      recurringSettings?.pricingModel || null,
      recurringSettings,
    );
  } else if (billingType === ItemBillingType.TermPlusRecurring) {
    return extractShowOriginalPrice(
      termSettings?.pricingModel || null,
      termSettings,
    );
  } else {
    return false;
  }
};

export function getPurchaseItemsPriceDetails(
  items: CustomerFacingCheckOutItemModel[],
  addOns: PurchaseCheckOutAddOnDetails[],
  surchargePercentage?: number,
  promotionRelatedData: PromotionModel | undefined = undefined,
): PurchaseItemsPriceModel[] {
  const prices: PurchaseItemsPriceModel[] = [];
  let isDiscountInPercentage =
    promotionRelatedData?.amountType === "Percentage" || false;
  let isDiscountInPercentageWithMax =
    (isDiscountInPercentage &&
      (promotionRelatedData?.maximumAmount || 0) > 0) ||
    !isDiscountInPercentage;
  let remainingDiscountAmount =
    (isDiscountInPercentage
      ? promotionRelatedData?.maximumAmount
      : promotionRelatedData?.amount) || 0;
  items?.map((item: CustomerFacingCheckOutItemModel) => {
    /* Items no longer carry an "Allow Surcharge" flag of their own, so the checkout/link setting
       applies to everything - mirroring the server, which charges the percentage on the whole
       purchase. The caller has already gated this on whether a surcharge applies at all. */
    const itemSurchargePercentage = surchargePercentage;
    const priceDetails = getPriceByBillingType(
      item.itemType,
      item.hasVariants,
      item.variants,
      item.price.id,
      item.price.billingType,
      item.price.terms,
      item.price.recurrence,
      item.quantity,
      item.selectedVariantIndex,
      item.customPricingInputValue
        ? parseFloat(item.customPricingInputValue)
        : undefined,
      itemSurchargePercentage,
      remainingDiscountAmount <= 0 && isDiscountInPercentageWithMax
        ? undefined
        : promotionRelatedData,
      isDiscountInPercentageWithMax,
    );
    if (!priceDetails) return;

    switch (item.price.billingType) {
      case ItemBillingType.TermPlusRecurring:
        // const startImmediately =
        //     item.price.recurrence.billingStartInterval === BillingStartFromInterval.Immediately;
        prices.push(
          generatePriceSummary(
            priceDetails,
            item.quantity,
            item.price.terms,
            item.price.recurrence,
            ItemBillingType.Term,
            false,
            false,
            itemSurchargePercentage,
            remainingDiscountAmount,
          ),
        );

        //if (startImmediately) {
        const clonedRecurrence = new RecurrencePriceSettings({
          ...item.price.recurrence,
          billingStartInterval: BillingStartFromInterval.After,
        });
        //calculate price for reucrrence that will start later
        const priceDetails1 = getPriceByBillingType(
          item.itemType,
          item.hasVariants,
          item.variants,
          item.price.id,
          ItemBillingType.Recurring,
          item.price.terms,
          clonedRecurrence,
          item.quantity,
          item.selectedVariantIndex,
          undefined,
          itemSurchargePercentage,
          undefined,
        );

        if (priceDetails1 != null) {
          prices.push(
            generatePriceSummary(
              priceDetails1,
              item.quantity,
              item.price.terms,
              item.price.recurrence,
              ItemBillingType.TermPlusRecurring,
              true,
              true,
              itemSurchargePercentage,
              remainingDiscountAmount,
            ),
          );
        }
        // }
        break;
      case ItemBillingType.Recurring:
        const clonedRecurrencee = new RecurrencePriceSettings({
          ...item.price.recurrence,
        });
        const priceDetailss1 = getPriceByBillingType(
          item.itemType,
          item.hasVariants,
          item.variants,
          item.price.id,
          ItemBillingType.Recurring,
          item.price.terms,
          clonedRecurrencee,
          item.quantity,
          item.selectedVariantIndex,
          undefined,
          itemSurchargePercentage,
          remainingDiscountAmount <= 0 && isDiscountInPercentageWithMax
            ? undefined
            : new PromotionModel({
                ...promotionRelatedData,
                maximumAmount: isDiscountInPercentage
                  ? remainingDiscountAmount
                  : promotionRelatedData?.maximumAmount,
                amount: !isDiscountInPercentage
                  ? remainingDiscountAmount
                  : promotionRelatedData?.amount,
              }),
          isDiscountInPercentageWithMax,
        );
        if (priceDetailss1 != null) {
          prices.push(
            generatePriceSummary(
              priceDetailss1,
              item.quantity,
              item.price.terms,
              item.price.recurrence,
              ItemBillingType.Recurring,
              item.price.recurrence.billingStartInterval ===
                BillingStartFromInterval.After,
              false,
              itemSurchargePercentage,
              remainingDiscountAmount,
            ),
          );
        }
        remainingDiscountAmount = Math.max(
          remainingDiscountAmount -
            (priceDetailss1?.discount || 0) * item.quantity,
          0,
        );
        break;
      default:
        prices.push(
          generatePriceSummary(
            priceDetails,
            item.quantity,
            item.price.terms,
            item.price.recurrence,
            item.price.billingType,
            false,
            false,
            itemSurchargePercentage,
            remainingDiscountAmount,
          ),
        );
        break;
    }

    remainingDiscountAmount = Math.max(
      remainingDiscountAmount - (priceDetails?.discount || 0),
      0,
    );

    if (item.itemType === "Donation" && item.payMonthly) {
      let title = `DUE EVERY 1 MONTH STARTING AFTER 1 MONTH`;
      let titleAsNode = (
        <CustomLabel className="font-15-ss-13">
          DUE <b>EVERY 1 MONTH</b> STARTING <b>AFTER 1 MONTH</b>
        </CustomLabel>
      );
      /* itemSurchargePercentage is a RATE. Assigning it straight to `surcharge` made a 3% fee on a
         $1,000 monthly donation show as $3.00 in the "due every month" block while the "due now"
         block above it correctly showed $30.00. Same formula getPriceByBillingType uses. */
      const surcharge = itemSurchargePercentage
        ? (itemSurchargePercentage * priceDetails.subTotal) / 100
        : 0;
      const tax = 0;
      const shipping = 0;

      let subTotal = priceDetails.subTotal;
      const discount = 0;
      prices.push(
        new PurchaseItemsPriceModel({
          title,
          discount,
          surcharge,
          tax,
          subTotal,
          shipping,
          dueLater: true,
          titleAsNode,
        }),
      );
    }
  });

  addOns?.length > 0 &&
    addOns.map((addOn: PurchaseCheckOutAddOnDetails) => {
      // Same rule as items: the surcharge covers the whole purchase, add-ons included.
      const addOnsurchargePercentage = surchargePercentage;

      const isTermOrTermPlusRecurring =
        addOn.billingType === "Term" ||
        addOn.billingType === "TermPlusRecurring";

      const priceDetails = getPriceByBillingType(
        addOn.itemType,
        addOn.hasVariants,
        addOn.selectedVariant || new ProductAttributeInputModel(),
        addOn.id,
        addOn.billingType,
        addOn.terms,
        addOn.recurrence,
        addOn.quantity,
        addOn.variantIndex(),
        addOn.customPricingInputValue
          ? parseFloat(addOn.customPricingInputValue)
          : undefined,
        addOnsurchargePercentage,
        remainingDiscountAmount <= 0 &&
          isDiscountInPercentageWithMax &&
          isTermOrTermPlusRecurring
          ? undefined
          : new PromotionModel({
              ...promotionRelatedData,
              maximumAmount: isDiscountInPercentage
                ? remainingDiscountAmount
                : promotionRelatedData?.maximumAmount,
              amount: !isDiscountInPercentage
                ? remainingDiscountAmount
                : promotionRelatedData?.amount,
            }),
        isDiscountInPercentageWithMax,
      );

      if (!priceDetails) return;

      switch (addOn.billingType) {
        case ItemBillingType.TermPlusRecurring:
          prices.push(
            generatePriceSummary(
              priceDetails,
              addOn.quantity,
              addOn.terms,
              addOn.recurrence,
              ItemBillingType.Term,
              false,
              false,
              addOnsurchargePercentage,
              remainingDiscountAmount,
            ),
          );
          const clonedRecurrence = new RecurrencePriceSettings({
            ...addOn.recurrence,
            billingStartInterval: BillingStartFromInterval.After,
          });
          const priceDetailsRecurring = getPriceByBillingType(
            addOn.itemType,
            addOn.hasVariants,
            addOn.selectedVariant || new ProductAttributeInputModel(),
            addOn.id,
            ItemBillingType.Recurring,
            addOn.terms,
            clonedRecurrence,
            addOn.quantity,
            addOn.variantIndex(),
            undefined,
            addOnsurchargePercentage,
            remainingDiscountAmount <= 0 && isDiscountInPercentageWithMax
              ? undefined
              : promotionRelatedData,
            isDiscountInPercentageWithMax,
          );

          if (priceDetailsRecurring != null) {
            prices.push(
              generatePriceSummary(
                priceDetailsRecurring,
                addOn.quantity,
                addOn.terms,
                addOn.recurrence,
                ItemBillingType.TermPlusRecurring,
                true,
                true,
                addOnsurchargePercentage,
                remainingDiscountAmount,
              ),
            );
          }
          break;

        case ItemBillingType.Recurring:
          const delayedRecurrence = new RecurrencePriceSettings({
            ...addOn.recurrence,
          });
          const delayedPriceDetails = getPriceByBillingType(
            addOn.itemType,
            addOn.hasVariants,
            addOn.selectedVariant || new ProductAttributeInputModel(),
            addOn.id,
            ItemBillingType.Recurring,
            addOn.terms,
            delayedRecurrence,
            addOn.quantity,
            addOn.variantIndex(),
            undefined,
            addOnsurchargePercentage,
            remainingDiscountAmount <= 0 && isDiscountInPercentageWithMax
              ? undefined
              : new PromotionModel({
                  ...promotionRelatedData,
                  maximumAmount: isDiscountInPercentage
                    ? remainingDiscountAmount
                    : promotionRelatedData?.maximumAmount,
                  amount: !isDiscountInPercentage
                    ? remainingDiscountAmount
                    : promotionRelatedData?.amount,
                }),
            isDiscountInPercentageWithMax,
          );
          if (delayedPriceDetails != null) {
            prices.push(
              generatePriceSummary(
                delayedPriceDetails,
                addOn.quantity,
                addOn.terms,
                addOn.recurrence,
                ItemBillingType.Recurring,
                delayedRecurrence.billingStartInterval ===
                  BillingStartFromInterval.After,
                false,
                addOnsurchargePercentage,
                remainingDiscountAmount,
              ),
            );
          }
          remainingDiscountAmount = Math.max(
            remainingDiscountAmount - (priceDetails?.discount || 0),
            0,
          );

          break;

        default:
          prices.push(
            generatePriceSummary(
              priceDetails,
              addOn.quantity,
              addOn.terms,
              addOn.recurrence,
              addOn.billingType,
              false,
              false,
              addOnsurchargePercentage,
              remainingDiscountAmount,
            ),
          );
          break;
      }
      if (isTermOrTermPlusRecurring) {
        remainingDiscountAmount = Math.max(
          remainingDiscountAmount - (priceDetails?.discount || 0),
          0,
        );
      }
    });

  return prices;

  function generatePriceSummary(
    priceDetails: PriceDescriptions,
    quantity: number,
    terms: PriceTermSettings,
    recurrence: RecurrencePriceSettings,
    billingType: keyof typeof ItemBillingType,
    dueLater?: boolean,
    excludeSetupFee: boolean = false,
    surchargePercentage?: number,
    remainingUsage?: number,
  ) {
    const surcharge = surchargePercentage
      ? (surchargePercentage * priceDetails.subTotal) / 100
      : 0;
    const tax = 0;
    const shipping = 0;

    let subTotal = priceDetails.subTotal;
    let discount = priceDetails.discount;
    if (remainingUsage && discount > remainingUsage) {
      discount = remainingUsage;
    }
    //  priceDetails?.priceWithDiscountInNumber
    //   ? subTotal - quantity * priceDetails?.priceWithDiscountInNumber
    //   : 0;

    let title = "DUE NOW";
    let titleAsNode = (
      <CustomLabel className="font-15-ss-13">
        DUE <b>NOW</b>
      </CustomLabel>
    );
    let billingStartText = "";
    switch (billingType) {
      case ItemBillingType.Recurring:
        if (excludeSetupFee) {
          subTotal = priceDetails.subTotalWithoutSetupFee;
        }
        if (
          dueLater ||
          recurrence.billingStartInterval ===
            BillingStartFromInterval.Immediately
        ) {
          updateBillingStartText();
          title = `DUE EVERY ${recurrence.billingIntervalCount} ${recurrence.billingInterval.toUpperCase()}${recurrence.billingIntervalCount > 1 ? "S" : ""} STARTING ${billingStartText}`;
          titleAsNode = (
            <CustomLabel className="font-15-ss-13">
              DUE{" "}
              <b>
                EVERY {recurrence.billingIntervalCount}{" "}
                {recurrence.billingInterval.toUpperCase()}
                {recurrence.billingIntervalCount > 1 ? "S" : ""}
              </b>{" "}
              STARTING <b>{billingStartText}</b>
            </CustomLabel>
          );
        }
        break;
      case ItemBillingType.Term:
        title = `DUE NOW`;
        titleAsNode = (
          <CustomLabel className="font-15-ss-13">
            DUE <b>NOW</b>
          </CustomLabel>
        );
        break;
      case ItemBillingType.TermPlusRecurring:
        dueLater
          ? updateBillingStartTextForTermsPlusRecurrence()
          : updateBillingStartText();
        title = `DUE EVERY ${recurrence.billingIntervalCount} ${recurrence.billingInterval.toUpperCase()}${recurrence.billingIntervalCount > 1 ? "S" : ""} STARTING ${billingStartText}`;
        titleAsNode = (
          <CustomLabel className="font-15-ss-13">
            DUE{" "}
            <b>
              EVERY {recurrence.billingIntervalCount}{" "}
              {recurrence.billingInterval.toUpperCase()}
              {recurrence.billingIntervalCount > 1 ? "S" : ""}{" "}
            </b>{" "}
            STARTING <b>{billingStartText}</b>
          </CustomLabel>
        );
        break;
    }

    return new PurchaseItemsPriceModel({
      title,
      discount,
      surcharge,
      tax,
      subTotal,
      shipping,
      dueLater: dueLater || false,
      titleAsNode,
    });

    function updateBillingStartText() {
      switch (recurrence.billingStartInterval) {
        case BillingStartFromInterval.Immediately:
          billingStartText = billingStartText + "NOW";
          break;
        case BillingStartFromInterval.After:
          switch (recurrence.billingStartAfterInterval) {
            case ItemBillingStartAfterInterval.Day:
              billingStartText =
                billingStartText +
                "AFTER " +
                recurrence.billingStartIntervalCount +
                ` DAY${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
              break;
            case ItemBillingStartAfterInterval.Month:
              billingStartText =
                billingStartText +
                "AFTER " +
                recurrence.billingStartIntervalCount +
                ` MONTH${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
              break;
            case ItemBillingStartAfterInterval.Week:
              billingStartText =
                billingStartText +
                "AFTER " +
                recurrence.billingStartIntervalCount +
                ` WEEK${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
              break;
            case ItemBillingStartAfterInterval.Year:
              billingStartText =
                billingStartText +
                "AFTER " +
                recurrence.billingStartIntervalCount +
                ` YEAR${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
              break;
          }
          break;
      }
    }

    function updateBillingStartTextForTermsPlusRecurrence() {
      switch (recurrence.billingStartAfterInterval) {
        case RecurringBillingInterval.Day:
          billingStartText =
            billingStartText +
            "AFTER " +
            recurrence.billingStartIntervalCount +
            ` DAY ${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
          break;
        case RecurringBillingInterval.Month:
          billingStartText =
            billingStartText +
            "AFTER " +
            recurrence.billingStartIntervalCount +
            ` MONTH ${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
          break;
        case RecurringBillingInterval.Week:
          billingStartText =
            billingStartText +
            "AFTER " +
            recurrence.billingStartIntervalCount +
            ` WEEK ${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
          break;
        case RecurringBillingInterval.Year:
          billingStartText =
            billingStartText +
            "AFTER " +
            recurrence.billingStartIntervalCount +
            ` YEAR ${recurrence.billingStartIntervalCount > 1 ? "S" : ""}`;
          break;
      }
    }
  }
}

export class PurchaseCheckOutAddOnDetails extends RelatedItemsPriceCategoriesModel {
  itemId: string;
  itemType: string;
  variantId: string;
  quantity: number;
  hasVariants: boolean;
  selectedVariant?: ProductAttributeInputModel;
  onlyAllowedVariantIds: string[];
  name: string;
  description: string;
  descriptionEditorState: EditorState;
  descriptionEditorRawContent: string;
  customPricingInputValue: string;
  media: MediaModel[];
  isTrackInventory: boolean;
  isOutOfStock: boolean;
  isIncludedItem: boolean;
  hasSurcharge: boolean;
  discount: boolean;
  review: boolean;
  return: boolean;
  tax: boolean;
  returnDays: number;
  errors: InputErrorModel<PurchaseCheckOutAddOnDetails>[];
  constructor(data?: Partial<PurchaseCheckOutAddOnDetails>) {
    super(data);
    this.variantId = data?.variantId || "";
    this.quantity = data?.quantity || 1;
    this.selectedVariant = data?.selectedVariant;
    this.itemId = data?.itemId || "";
    this.itemType = data?.itemType || "";
    this.hasVariants = data?.hasVariants === true;
    this.hasSurcharge = data?.hasSurcharge || false;
    this.discount = data?.discount || false;
    this.review = data?.review || false;
    this.return = data?.return || false;
    this.tax = data?.tax || false;
    this.returnDays = data?.returnDays || 0;
    this.onlyAllowedVariantIds = data?.onlyAllowedVariantIds || [];
    this.descriptionEditorRawContent = data?.descriptionEditorRawContent || "";
    this.name = data?.name || "";
    this.description = data?.description || "";
    this.isIncludedItem = data?.isIncludedItem || false;
    this.media = data?.media?.map((i) => new MediaModel(i)) || [];
    this.descriptionEditorState =
      data?.descriptionEditorState || EditorState.createEmpty();
    this.customPricingInputValue = data?.customPricingInputValue || "";
    this.errors = data?.errors?.map((i) => new InputErrorModel(i)) || [];
    this.isTrackInventory = data?.isTrackInventory || false;
    this.isOutOfStock = data?.isOutOfStock || false;
    autorun(() => {
      this.validate(true);
    });
  }

  getAllMedias(firstTryItemMedia?: boolean) {
    if (this.hasVariants) {
      const option =
        this.selectedVariant?.options
          ?.map((m) => m.attributeMappings)
          .reduce((acc, val) => acc.concat(val), []) || [];
      const medias = option
        .map((m) => m.medias)
        .reduce((acc, val) => acc.concat(val), []);

      const uniqueMedia = medias.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.mediaId === value.mediaId),
      );
      if (firstTryItemMedia) {
        return this.media?.length > 0 ? this.media : uniqueMedia;
      } else {
        return uniqueMedia?.length > 0 ? uniqueMedia : this.media;
      }
    } else {
      return this.media;
    }
  }

  getAddOnMedia() {
    if (this.hasVariants) {
      const index = this.variantIndex();
      if (index !== undefined && index > -1 && this.selectedVariant) {
        const option = this.selectedVariant.options[index];
        const combinedMedia = option.attributeMappings
          .map((m) => m.medias)
          .reduce((acc, val) => acc.concat(val), []);
        const uniqueMedia = combinedMedia.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.mediaId === value.mediaId),
        );
        return uniqueMedia?.length > 0 ? uniqueMedia : this.media;
      } else {
        return this.getAllMedias(true);
      }
    } else {
      return this.media;
    }
  }

  variantIndex() {
    return this.selectedVariant?.options?.findIndex(
      (i) => i.id === this.variantId,
    );
  }

  isCustomPricing() {
    let isCustomPricing = false;
    switch (this.billingType) {
      case ItemBillingType.Term:
        isCustomPricing = this.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.TermPlusRecurring:
        isCustomPricing = this.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.Recurring:
        isCustomPricing = this.recurrence.pricingModel === "Custom";
        break;
      default:
        break;
    }
    return isCustomPricing;
  }

  getCustomPricing() {
    let customPricing: CustomPricingModel | undefined | null = null;
    switch (this.billingType) {
      case ItemBillingType.Term:
        customPricing = this.terms.customPricing;
        break;
      case ItemBillingType.TermPlusRecurring:
        customPricing = this.terms.customPricing;
        break;
      case ItemBillingType.Recurring:
        customPricing = this.recurrence.customPricing;
        break;
      default:
        break;
    }
    return customPricing;
  }

  hasError(field: keyof PurchaseCheckOutAddOnDetails): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false) {
    const newErrors: InputErrorModel<PurchaseCheckOutAddOnDetails>[] = [];
    if (this.isCustomPricing()) {
      const isValid = CoreInputModelUtil.validateDecimalValue(
        this.customPricingInputValue,
        "customPricingInputValue",
        newErrors,
        undefined,
        true,
      );
      if (isValid) {
        const customPricing = this.getCustomPricing();
        if (
          customPricing?.minimum &&
          parseFloat(this.customPricingInputValue) < customPricing.minimum &&
          customPricing.minimum
        ) {
          newErrors.push(
            new InputErrorModel<PurchaseCheckOutAddOnDetails>({
              field: "customPricingInputValue",
              message: `Minimum price is $${AccountSettingsStore.formatAmount(customPricing.minimum)}`,
            }),
          );
        }

        if (
          customPricing?.maximum &&
          parseFloat(this.customPricingInputValue) > customPricing.maximum &&
          customPricing.maximum
        ) {
          newErrors.push(
            new InputErrorModel<PurchaseCheckOutAddOnDetails>({
              field: "customPricingInputValue",
              message: `Maximum price is $${AccountSettingsStore.formatAmount(customPricing.maximum)}`,
            }),
          );
        }
      }
    }
    if (!CoreInputModelUtil.validateDigit(this.quantity.toString())) {
      CoreInputModelUtil.addError(
        "quantity",
        newErrors,
        "Quantity must be a number",
      );
    } else {
      if (parseFloat(this.quantity.toString()) < 1) {
        CoreInputModelUtil.addError(
          "quantity",
          newErrors,
          "Quantity must be greater than 0",
        );
      }
    }
    if (this.hasVariants) {
      const index = this.variantIndex();
      const selectedVariant =
        index != undefined &&
        index > -1 &&
        this.selectedVariant?.options[index];
      if (!selectedVariant) {
        CoreInputModelUtil.addError(
          "variantId",
          newErrors,
          "Please select a variant",
        );
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  getError(field: keyof PurchaseCheckOutAddOnDetails): string {
    return this.errors.find((e) => e.field === field)?.message || "";
  }
}

export class CustomerFacingCheckOutItemModel {
  id: string;
  name: string;
  description: string;
  descriptionEditorRawContent: string;
  itemType: string;
  itemId: string;
  quantity: number;
  hasSurcharge: boolean;
  purchaseLinkSurcharge: boolean;
  discount: boolean;
  review: boolean;
  return: boolean;
  tax: boolean;
  returnDays: number;
  price: RelatedItemsPriceCategoriesModel;
  prices: RelatedItemsPriceCategoriesModel[];
  medias: MediaModel[];
  donationIncludedDetails?: DonationIncludedSectionModel;
  includedItems?: RelatedItemsCategoriesModel[];
  requiredItems?: RelatedItemsCategoriesModel[];
  recommendedItems?: RelatedItemsCategoriesModel[];
  //this field will be updated from this getAllowedIncludedItems only from the first step
  includedItemsByCondition?: RelatedItemsCategoriesModel[];
  donationIncludedItemsByRangeCategories?: RelatedItemsCategoriesModel[];
  relatedItems: RelatedSectionModel | null;
  selectedDisplayType: keyof typeof CheckOutDisplayTypes;
  hasVariants: boolean;
  variants: ProductAttributeInputModel;
  selectedVariantIndex: number;
  //this is for sorting purpose only
  priceInNumber: number;
  //this is for donation purchase
  donationAmount: number;
  customPricingInputValue: string;
  errors: InputErrorModel<CustomerFacingCheckOutItemModel>[];
  inMemoryOf: boolean;
  dedicatedTo: string;
  tributeMessage: string;
  beneficiaries: BeneficiaryModel[];
  payMonthly: boolean;
  allowAnnualIncrease: boolean;
  annualIncreasePercentage: number;
  coverFee: boolean;
  anonymousDonation: boolean;
  coverFeeMessage: string;
  isTrackInventory: boolean;
  inventorySettings?: InventorySettings;
  isActive: boolean;
  constructor(data?: Partial<CustomerFacingCheckOutItemModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.description = data?.description || "";
    this.itemId = data?.itemId || "";
    this.itemType = data?.itemType || "";
    this.quantity = data?.quantity || 1;
    this.hasSurcharge = data?.hasSurcharge || false;
    this.discount = data?.discount || false;
    this.review = data?.review || false;
    this.return = data?.return || false;
    this.purchaseLinkSurcharge = data?.purchaseLinkSurcharge || false;
    this.tax = data?.tax || false;
    this.returnDays = data?.returnDays || 0;
    this.price = new RelatedItemsPriceCategoriesModel(data?.price);
    this.medias = data?.medias?.map((m) => new MediaModel(m)) || [];
    this.prices =
      data?.prices?.map(
        (price) => new RelatedItemsPriceCategoriesModel(price),
      ) || [];
    this.relatedItems = data?.relatedItems
      ? new RelatedSectionModel(data?.relatedItems)
      : null;
    this.requiredItems =
      (data?.requiredItems &&
        data?.requiredItems.map((i) => {
          return new RelatedItemsCategoriesModel(i);
        })) ||
      [];
    this.recommendedItems =
      (data?.recommendedItems &&
        data?.recommendedItems.map((i) => {
          return new RelatedItemsCategoriesModel(i);
        })) ||
      [];
    this.donationIncludedItemsByRangeCategories =
      data?.donationIncludedItemsByRangeCategories
        ? data?.donationIncludedItemsByRangeCategories.map(
            (i) => new RelatedItemsCategoriesModel(i),
          )
        : [];
    this.includedItems =
      (data?.includedItems &&
        data?.includedItems?.map((i) => new RelatedItemsCategoriesModel(i))) ||
      [];
    this.includedItemsByCondition =
      (data?.includedItemsByCondition &&
        data?.includedItemsByCondition.map(
          (i) => new RelatedItemsCategoriesModel(i),
        )) ||
      [];
    this.descriptionEditorRawContent = data?.descriptionEditorRawContent || "";
    this.hasVariants = (data && data?.hasVariants) == true;
    this.variants =
      (data && data?.variants) || new ProductAttributeInputModel();
    if (data?.selectedVariantIndex === undefined) {
      this.selectedVariantIndex = -1;
    } else {
      this.selectedVariantIndex = data?.selectedVariantIndex;
    }

    this.selectedDisplayType =
      data?.selectedDisplayType || CheckOutDisplayTypes.SingleItem;
    this.donationAmount = data?.donationAmount || 0;
    this.customPricingInputValue = data?.customPricingInputValue || "";
    this.errors = data?.errors?.map((e) => new InputErrorModel(e)) || [];
    this.inMemoryOf = data?.inMemoryOf || false;
    this.dedicatedTo = data?.dedicatedTo || "";
    this.tributeMessage = data?.tributeMessage || "";
    this.beneficiaries = data?.beneficiaries || [];
    this.payMonthly = data?.payMonthly || false;
    this.allowAnnualIncrease = data?.allowAnnualIncrease || false;
    this.annualIncreasePercentage = data?.annualIncreasePercentage || 0;
    this.coverFee = data?.coverFee || false;
    this.anonymousDonation = data?.anonymousDonation || false;
    this.coverFeeMessage = data?.coverFeeMessage || "";
    this.isTrackInventory = data?.isTrackInventory || false;
    this.inventorySettings =
      data?.inventorySettings && new InventorySettings(data?.inventorySettings);
    this.isActive = data?.isActive || false;
    if (this.price && this.price) {
      //this is just for sorting
      const priceDescription = getPriceByBillingType(
        this.itemType,
        this.hasVariants,
        this.variants,
        this.price.id,
        this.price.billingType,
        this.price.terms,
        this.price.recurrence,
        this.quantity,
        0,
      );

      this.priceInNumber = priceDescription?.priceInNumber || 0;
    } else {
      this.priceInNumber = 0;
    }

    this.donationIncludedDetails = data?.donationIncludedDetails
      ? new DonationIncludedSectionModel(data?.donationIncludedDetails)
      : undefined;

    autorun(() => {
      this.validate(true);
    });
  }

  get isItemUnavailable() {
    if (!this.isActive) return true;
    if (this.isTrackInventory) {
      if (this.hasVariants) {
        const options = this.variants.options.filter(
          (o) => o.isAvailableBasedOnOnHand,
        );
        return options.length === 0;
      } else {
        return this.inventorySettings && this.inventorySettings?.available <= 0;
      }
    }
    return this.inventorySettings ? !this.inventorySettings.isInStock : false;
  }

  get getItemMedia() {
    if (this.hasVariants) {
      if (this.selectedVariantIndex > -1) {
        const option = this.variants.options[this.selectedVariantIndex];
        if (option && option.attributeMappings?.length > 0) {
          const combinedMedia = option.attributeMappings
            .map((m) => m.medias)
            .reduce((acc, val) => acc.concat(val), []);

          const uniqueMedia = combinedMedia.filter(
            (value, index, self) =>
              index === self.findIndex((t) => t.mediaId === value.mediaId),
          );
          return uniqueMedia?.length > 0 ? uniqueMedia : this.medias;
        }
      } else {
        return this.medias;
      }

      return [];
    } else {
      return this.medias;
    }
  }

  getAllMedias(firstTryItemMedia?: boolean) {
    if (this.hasVariants) {
      const option = this.variants.options
        .map((m) => m.attributeMappings)
        .reduce((acc, val) => acc.concat(val), []);
      const medias = option
        .map((m) => m.medias)
        .reduce((acc, val) => acc.concat(val), []);

      const uniqueMedia = medias.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.mediaId === value.mediaId),
      );
      if (firstTryItemMedia) {
        return this.medias?.length > 0 ? this.medias : uniqueMedia;
      } else {
        return uniqueMedia?.length > 0 ? uniqueMedia : this.medias;
      }
    } else {
      return this.medias;
    }
  }

  static deserializeAPIModel(
    data: IAPICustomerFacingCheckOutItemModel,
    isDonation: boolean = false,
    purchaseLinkSurcharge: boolean = false,
    donationData?: IAPIGetEditDonationModel,
    selectedVariantIds?: string[],
  ) {
    const mappedData: Partial<CustomerFacingCheckOutItemModel> = {
      id: data.id,
      isActive: data.isActive,
      name: data.name,
      description: data.description,
      itemId: data.itemId,
      itemType: data.itemType,
      quantity: data.quantity,
      hasSurcharge: data.surcharge,
      tax: data.tax,
      discount: data.discount,
      review: data.review,
      return: data.return,
      returnDays: data.returnDays,
      purchaseLinkSurcharge: purchaseLinkSurcharge,
      price: isDonation
        ? new RelatedItemsPriceCategoriesModel()
        : data.price
          ? RelatedItemsPriceCategoriesModel.deserializeAPIModelFromPriceCreateUpdate(
              data?.price,
            )
          : data.prices?.length > 0
            ? RelatedItemsPriceCategoriesModel.deserializeAPIModelFromPriceCreateUpdate(
                data?.prices[0],
              )
            : new RelatedItemsPriceCategoriesModel(),
      prices: isDonation
        ? []
        : data.prices?.map((price) =>
            RelatedItemsPriceCategoriesModel.deserializeAPIModelFromPriceCreateUpdate(
              price,
            ),
          ) || [],
      medias: data.medias?.map((m) => MediaModel.deserializeAPIModel(m)) || [],
      // relatedItems: data?.relatedItems ? RelatedSectionModel.deserializeAPIModel(data?.relatedItems) : null,
      requiredItems:
        (data.requiredItems &&
          data?.requiredItems?.map((i) =>
            RelatedItemsCategoriesModel.deserializeAPIModel(i),
          )) ||
        [],
      donationIncludedDetails: donationData?.options?.included
        ? DonationIncludedSectionModel.deserializeAPIModel(
            donationData.options.included,
          )
        : undefined,
      donationIncludedItemsByRangeCategories:
        data.donationRangeIncludedItems?.map((i) =>
          RelatedItemsCategoriesModel.deserializeAPIModel(i),
        ) || [],
      recommendedItems: data.recommendedItems
        ? data.recommendedItems.map((i) =>
            RelatedItemsCategoriesModel.deserializeAPIModel(i),
          )
        : [],
      includedItems: data.includedItems
        ? data.includedItems.map((i) =>
            RelatedItemsCategoriesModel.deserializeAPIModel(i),
          )
        : [],
      descriptionEditorRawContent: data.descriptionEditorRawContent || "",
      relatedItems: data.relatedItems
        ? RelatedSectionModel.deserializeAPIModel(data.relatedItems)
        : null,
      hasVariants: data.hasVariants,
      variants: data.variants
        ? selectedVariantIds
          ? ProductAttributeInputModel.deserializeAPIWithOptions(
              data.variants,
              true,
              selectedVariantIds,
            )
          : ProductAttributeInputModel.deserializeAPI(data.variants, true)
        : new ProductAttributeInputModel(),
      isTrackInventory: data.isTrackInventory || false,
      inventorySettings:
        data.inventorySettings && new InventorySettings(data.inventorySettings),
    };
    return new CustomerFacingCheckOutItemModel(mappedData);
  }

  static deserializeFromCheckOutItemsModel(
    data: CheckOutItemsModel,
    donationDetail?: ItemDetailInputModel,
  ): CustomerFacingCheckOutItemModel {
    const selectedPrice = data.itemDetails.prices.find(
      (price) => price.id === data.priceId,
    );
    const mappedData: Partial<CustomerFacingCheckOutItemModel> = {
      id: data.id,
      name: data.name,
      description: data.itemDetails.description,
      descriptionEditorRawContent: data.itemDetails.descriptionEditorRawContent,
      itemId: data.itemId,
      itemType: data.itemDetails.type,
      quantity: data.quantity,
      price: selectedPrice,
      medias:
        data.itemDetails.medias?.map((m) =>
          MediaModel.deserializeAPIModel(m),
        ) || [],
      requiredItems:
        data.itemDetails.requiredVariantIds?.map((id) => {
          const relatedItem = new RelatedItemsCategoriesModel(data.itemDetails);
          relatedItem.selectedVariantIds = [id];
          return relatedItem;
        }) || [],
      recommendedItems:
        data.itemDetails.recommendedVariantIds?.map((id) => {
          const relatedItem = new RelatedItemsCategoriesModel(data.itemDetails);
          relatedItem.selectedVariantIds = [id];
          return relatedItem;
        }) || [],
      relatedItems: null,
      hasVariants: data.itemDetails.hasVariants,
      variants: data.itemDetails.variants,
      // Legacy item level "Allow Surcharge" flag. It is no longer consulted when
      // computing or displaying the surcharge - the checkout/link setting alone
      // decides - but is still carried so existing data round trips unchanged.
      hasSurcharge: data.allowsSurcharge(donationDetail),
      selectedDisplayType: CheckOutDisplayTypes.SingleItem,
      isActive: data.isActive,
    };
    return new CustomerFacingCheckOutItemModel(mappedData);
  }

  static groupByBillingFrequency(
    items: CustomerFacingCheckOutItemModel[],
    customOption?: CustomOptionModel,
  ): PurchaseItemsByBillingFrequency[] {
    const groupedItems: PurchaseItemsByBillingFrequency[] = [];
    items.forEach((item) => {
      const billingFrequency = item.price.billingType;

      let groupedName = "OTHER";
      switch (billingFrequency) {
        case ItemBillingType.Term:
          groupedName = "ONE TIME";
          break;
        case ItemBillingType.TermPlusRecurring:
        case ItemBillingType.Recurring:
          if (item.price.recurrence) {
            switch (item.price.recurrence.billingInterval) {
              case RecurringBillingInterval.Day:
                groupedName = "DAILY";
                break;
              case RecurringBillingInterval.Week:
                groupedName = "WEEKLY";
                break;
              case RecurringBillingInterval.Month:
                groupedName = "MONTHLY";
                break;
              case RecurringBillingInterval.Year:
                groupedName = "YEARLY";
                break;
            }
          }
          break;

        default:
          break;
      }
      const existingGroup = groupedItems.find((g) => g.name === groupedName);
      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        groupedItems.push(
          new PurchaseItemsByBillingFrequency({
            name: groupedName,
            items: [item],
          }),
        );
      }
    });

    if (customOption) {
      groupedItems.push(
        new PurchaseItemsByBillingFrequency({ name: "CUSTOM" }),
      );
    }

    return groupedItems;
  }

  isCustomPricing() {
    let isCustomPricing = false;
    switch (this.price.billingType) {
      case ItemBillingType.Term:
        isCustomPricing = this.price.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.TermPlusRecurring:
        isCustomPricing = this.price.terms.pricingModel === "Custom";
        break;
      case ItemBillingType.Recurring:
        isCustomPricing = this.price.recurrence.pricingModel === "Custom";
        break;
      default:
        break;
    }
    return isCustomPricing;
  }

  getCustomPricing() {
    let customPricing: CustomPricingModel | undefined | null = null;
    switch (this.price.billingType) {
      case ItemBillingType.Term:
        customPricing = this.price.terms.customPricing;
        break;
      case ItemBillingType.TermPlusRecurring:
        customPricing = this.price.terms.customPricing;
        break;
      case ItemBillingType.Recurring:
        customPricing = this.price.recurrence.customPricing;
        break;
      default:
        break;
    }
    return customPricing;
  }

  hasError(key: keyof CustomerFacingCheckOutItemModel): boolean {
    return this.errors.find((f) => f.field == key) !== undefined;
  }

  getError(key: keyof CustomerFacingCheckOutItemModel): string {
    const error = this.errors.find((f) => f.field == key);
    return error?.message || "";
  }

  validate(isAutoRun = false) {
    const newErrors: InputErrorModel<CustomerFacingCheckOutItemModel>[] = [];
    if (this.isCustomPricing()) {
      const isValid = CoreInputModelUtil.validateDecimalValue(
        this.customPricingInputValue,
        "customPricingInputValue",
        newErrors,
        undefined,
        true,
      );
      if (isValid) {
        const customPricing = this.getCustomPricing();
        if (
          customPricing?.minimum &&
          parseFloat(this.customPricingInputValue) < customPricing.minimum &&
          customPricing.minimum
        ) {
          newErrors.push(
            new InputErrorModel<CustomerFacingCheckOutItemModel>({
              field: "customPricingInputValue",
              message: `Minimum price is $${AccountSettingsStore.formatAmount(customPricing.minimum)}`,
            }),
          );
        }

        if (
          customPricing?.maximum &&
          parseFloat(this.customPricingInputValue) > customPricing.maximum &&
          customPricing.maximum
        ) {
          newErrors.push(
            new InputErrorModel<CustomerFacingCheckOutItemModel>({
              field: "customPricingInputValue",
              message: `Maximum price is $${AccountSettingsStore.formatAmount(customPricing.maximum)}`,
            }),
          );
        }
      }
    }

    if (!CoreInputModelUtil.validateDigit(this.quantity.toString())) {
      CoreInputModelUtil.addError(
        "quantity",
        newErrors,
        "Quantity must be a number",
      );
    } else {
      if (parseFloat(this.quantity.toString()) < 1) {
        CoreInputModelUtil.addError(
          "quantity",
          newErrors,
          "Quantity must be greater than 0",
        );
      }
    }
    if (this.hasVariants) {
      const selectedVariant = this.variants.options[this.selectedVariantIndex];
      if (!selectedVariant) {
        CoreInputModelUtil.addError(
          "selectedVariantIndex",
          newErrors,
          "Please select a variant",
        );
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  getAllowedIncludedItems() {
    if (this.itemType !== "Donation") {
      return this.includedItems;
    }
    if (!this.donationIncludedDetails) return [];

    if (this.donationIncludedDetails.any) return this.includedItems;

    if (!this.donationIncludedDetails.ranges) return [];

    let range = this.donationIncludedDetails.ranges.find(
      (r) =>
        this.donationAmount >= parseFloat(r.start) &&
        this.donationAmount <= parseFloat(r.max),
    );

    if (!range && this.donationIncludedDetails.ranges.length > 0) {
      range =
        this.donationIncludedDetails.ranges[
          this.donationIncludedDetails.ranges.length - 1
        ];
    }

    if (!range) return [];
    if (range.item.showSelectItems) return range.item.items || [];

    if (range.item.showCategories) {
      return (
        this.donationIncludedItemsByRangeCategories?.filter((item) => {
          const matchingIndex = range?.item?.categories?.findIndex(
            (c) => item?.category && c.id === item?.category?.id,
          );
          return matchingIndex !== undefined && matchingIndex > -1;
        }) || []
      );
    }

    return [];
  }

  getAllowedRequiredItems() {
    //remove current item if it is in required items
    return (
      this.requiredItems?.filter(
        (r) =>
          r.type != this.itemType ||
          (r.type == this.itemType && r.id != this.id),
      ) || []
    );
  }

  getAllowedRecommendedItems() {
    //remove current item if it is in recommended items
    return (
      this.recommendedItems?.filter(
        (r) =>
          r.type != this.itemType ||
          (r.type === this.itemType && r.id != this.id),
      ) || []
    );
  }
}

export class PurchaseItemsByBillingFrequency {
  name: string;
  items: CustomerFacingCheckOutItemModel[];
  constructor(data?: Partial<PurchaseItemsByBillingFrequency>) {
    this.name = data?.name || "";
    this.items =
      data?.items?.map((i) => new CustomerFacingCheckOutItemModel(i)) || [];
  }
}
